<script>
    export let options = [];
    export let value = "";
    export let placeholder = "Type to search...";
    export let label = "";
    
    let searchTerm = value;
    let lastSyncedValue = value;
    let isOpen = false;
    let highlightedIndex = -1;
    let inputElement;

    $: filteredOptions = options.filter(opt => 
        opt.toLowerCase().includes(searchTerm.toLowerCase())
    );

    function selectOption(option) {
        value = option;
        searchTerm = option;
        lastSyncedValue = option;
        isOpen = false;
        highlightedIndex = -1;
    }

    function handleInput(e) {
        searchTerm = e.target.value;
        // Editing a committed option clears the bound selection without
        // replacing what the user is currently typing.
        value = options.includes(searchTerm) ? searchTerm : '';
        lastSyncedValue = value;
        isOpen = true;
        highlightedIndex = -1;
    }

    function clearSelection() {
        value = '';
        searchTerm = '';
        lastSyncedValue = '';
        highlightedIndex = -1;
        isOpen = true;
        inputElement?.focus();
    }

    function handleKeydown(e) {
        if (!isOpen && e.key !== 'Enter') {
            isOpen = true;
            return;
        }

        if (e.key === 'ArrowDown') {
            e.preventDefault();
            highlightedIndex = Math.min(highlightedIndex + 1, filteredOptions.length - 1);
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            highlightedIndex = Math.max(highlightedIndex - 1, -1);
        } else if (e.key === 'Enter' && highlightedIndex >= 0) {
            e.preventDefault();
            selectOption(filteredOptions[highlightedIndex]);
        } else if (e.key === 'Escape') {
            isOpen = false;
            highlightedIndex = -1;
        }
    }

    function handleFocus() {
        isOpen = true;
    }

    function handleBlur() {
        // Delay to allow click events to fire
        setTimeout(() => {
            isOpen = false;
            if (options.includes(searchTerm)) {
                value = searchTerm;
                lastSyncedValue = searchTerm;
            }
        }, 200);
    }

    // Synchronize only genuine parent-driven value changes. User edits update
    // lastSyncedValue themselves and therefore are not overwritten here.
    $: if (value !== lastSyncedValue) {
        searchTerm = value;
        lastSyncedValue = value;
    }
</script>

<div class="autocomplete-wrapper">
    {#if label}
        <label class="autocomplete-label">{label}</label>
    {/if}
    <div class="autocomplete-container">
        <input
            bind:this={inputElement}
            type="text"
            bind:value={searchTerm}
            on:input={handleInput}
            on:keydown={handleKeydown}
            on:focus={handleFocus}
            on:blur={handleBlur}
            placeholder={placeholder}
            class="autocomplete-input"
            role="combobox"
            aria-expanded={isOpen}
            aria-autocomplete="list"
        />
        {#if searchTerm}
            <button
                type="button"
                class="autocomplete-clear"
                aria-label="Clear {label || 'selection'}"
                title="Clear selection"
                on:mousedown={(e) => e.preventDefault()}
                on:click={clearSelection}
            >×</button>
        {/if}
        {#if isOpen && filteredOptions.length > 0}
            <ul class="autocomplete-dropdown" role="listbox">
                {#each filteredOptions as option, index}
                    <li
                        class="autocomplete-option"
                        class:highlighted={index === highlightedIndex}
                        on:click={() => selectOption(option)}
                        on:mousedown={(e) => e.preventDefault()}
                        role="option"
                        aria-selected={index === highlightedIndex}
                    >
                        {@html option.replace(
                            new RegExp(`(${searchTerm})`, 'gi'),
                            '<mark>$1</mark>'
                        )}
                    </li>
                {/each}
            </ul>
        {:else if isOpen && searchTerm && filteredOptions.length === 0}
            <ul class="autocomplete-dropdown">
                <li class="autocomplete-option no-results">No results found</li>
            </ul>
        {/if}
    </div>
    <input type="hidden" bind:value={value} />
</div>

<style>
    .autocomplete-wrapper {
        position: relative;
        width: 100%;
    }

    .autocomplete-label {
        display: block;
        margin-bottom: 0.5em;
        font-weight: 600;
        color: #374151;
    }

    .autocomplete-container {
        position: relative;
    }

    .autocomplete-input {
        width: 100%;
        padding: 0.75em 2.75em 0.75em 1em;
        border: 2px solid #e5e7eb;
        border-radius: 8px;
        font-size: 1em;
        background-color: white;
        color: #111827;
        transition: all 0.2s ease;
        box-sizing: border-box;
    }

    .autocomplete-clear {
        position: absolute;
        top: 50%;
        right: 0.65em;
        width: 1.8em;
        height: 1.8em;
        padding: 0;
        transform: translateY(-50%);
        border: 0;
        border-radius: 50%;
        background: #eef2f7;
        color: #475569;
        cursor: pointer;
        font-size: 1.1em;
        line-height: 1;
    }

    .autocomplete-clear:hover,
    .autocomplete-clear:focus-visible {
        background: #e2e8f0;
        color: #111827;
        outline: 2px solid #fab45a;
        outline-offset: 1px;
    }

    .autocomplete-input:focus {
        outline: none;
        border-color: #fab45a;
        box-shadow: 0 0 0 3px rgba(250, 180, 90, 0.1);
    }

    .autocomplete-dropdown {
        position: absolute;
        top: 100%;
        left: 0;
        right: 0;
        margin-top: 4px;
        padding: 0;
        list-style: none;
        background: white;
        border: 2px solid #e5e7eb;
        border-radius: 8px;
        box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
        max-height: 200px;
        overflow-y: auto;
        z-index: 1000;
    }

    .autocomplete-option {
        padding: 0.75em 1em;
        cursor: pointer;
        transition: background-color 0.15s ease;
    }

    .autocomplete-option:hover,
    .autocomplete-option.highlighted {
        background-color: #f3f4f6;
    }

    .autocomplete-option mark {
        background-color: #fab45a;
        color: inherit;
        font-weight: 600;
        padding: 0;
    }

    .autocomplete-option.no-results {
        color: #6b7280;
        font-style: italic;
        cursor: default;
    }

    .autocomplete-option.no-results:hover {
        background-color: transparent;
    }
</style>

